import { useEffect, useState } from 'react';
import { Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './BookSessionCta.css';

/**
 * Casa de Candy — persistent "Book Session Now" CTA.
 *
 * Floats bottom-center on every public surface (home + client landing).
 * Hides itself when the booking section is already in view to avoid
 * doubling the call-to-action. Smooth-scrolls to `targetId` on click,
 * or routes to a different page when `routeTo` is provided.
 */
export function BookSessionCta({ targetId = 'book', routeTo, label = 'Book Session Now' }) {
  const [hidden, setHidden] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (routeTo) return; // when routing across pages, no in-view detection
    const target = document.getElementById(targetId);
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => setHidden(entry.isIntersecting));
      },
      { threshold: 0.25 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [targetId, routeTo]);

  const onClick = (e) => {
    e.preventDefault();
    if (routeTo) {
      // Support paths like '/client#book' — navigate then smooth-scroll on mount.
      const [path, hash] = routeTo.split('#');
      navigate(path);
      if (hash) {
        // Wait for the next route to mount before scrolling.
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 350);
      }
      return;
    }
    const el = document.getElementById(targetId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <a
      href={routeTo || `#${targetId}`}
      onClick={onClick}
      className={`book-session-cta ${hidden ? 'is-hidden' : ''}`}
      data-testid="book-session-cta"
      aria-label={label}
    >
      <span className="book-session-cta__halo" aria-hidden />
      <Calendar className="book-session-cta__icon" />
      <span className="book-session-cta__label">{label}</span>
    </a>
  );
}

export default BookSessionCta;
